import {
    BookOpen,
    Brain,
    FlaskConical,
    MessageSquare,
    Play,
    Plus,
    Search,
    Square,
    TrendingUp,
    Trophy,
    Users,
    X,
    Zap
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import MainLayout from '../components/layout/MainLayout'
import { API_URL, apiFetch } from '../config/api'
import { useAuth } from '../contexts/AuthContext'

interface ResearchProject {
  id: number
  title: string
  description: string
  category: string
  status: 'active' | 'paused' | 'completed' | 'planning' | 'pending'
  project_type: string
  visibility?: string
  is_private?: boolean
  is_restricted?: boolean
  budget_flz: number
  spent_flz: number
  total_findings: number
  total_questions: number
  total_contributions: number
  total_participants: number
  agent_count: number
  task_count: number
  owner_id?: number
  owner_name: string
  last_activity?: string | null
  created_at: string
}

const CATEGORY_COLORS: Record<string, string> = {
  Physics: '#6366f1',
  Chemistry: '#10b981',
  Biology: '#f59e0b',
  'AI/ML': '#3b82f6',
  Mathematics: '#8b5cf6',
  Engineering: '#ef4444',
  Astronomy: '#06b6d4',
  Neuroscience: '#ec4899',
  'Climate Science': '#84cc16',
  'Data Science': '#f97316',
}

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  Physics: <Zap size={14} />,
  Chemistry: <FlaskConical size={14} />,
  Biology: <Brain size={14} />,
  'AI/ML': <Brain size={14} />,
  Mathematics: <TrendingUp size={14} />,
  Astronomy: <Zap size={14} />,
}

function getCategoryColor(cat: string) {
  return CATEGORY_COLORS[cat] || '#6366f1'
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; color: string }> = {
    active: { label: 'Active', color: '#10b981' },
    paused: { label: 'Paused', color: '#f59e0b' },
    completed: { label: 'Completed', color: '#3b82f6' },
    planning: { label: 'Planning', color: '#8b5cf6' },
    pending: { label: 'Pending', color: '#6b7280' },
  }
  const s = map[status] || { label: status, color: 'var(--secondary)' }
  return (
    <span style={{
      background: s.color + '22',
      color: s.color,
      border: `1px solid ${s.color}44`,
      padding: '2px 10px',
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.03em'
    }}>
      {s.label}
    </span>
  )
}

function ResearchCard({ project, onClick, onStart, onStop }: { project: ResearchProject; onClick: () => void; onStart?: (e: React.MouseEvent) => void; onStop?: (e: React.MouseEvent) => void }) {
  const catColor = getCategoryColor(project.category)
  const budgetUsed = project.budget_flz > 0
    ? Math.min(100, Math.round((project.spent_flz / project.budget_flz) * 100))
    : 0
  const isPrivate = project.is_private || project.visibility === 'private'
  const isRestricted = project.is_restricted || project.visibility === 'restricted'

  return (
    <div
      onClick={isPrivate ? undefined : onClick}
      style={{
        background: 'var(--card-bg)',
        border: `1px solid ${isPrivate ? '#ef4444' : isRestricted ? '#f59e0b' : 'var(--border-color)'}`,
        borderRadius: 16,
        filter: isPrivate ? 'blur(3px)' : 'none',
        opacity: isPrivate ? 0.6 : 1,
        cursor: isPrivate ? 'not-allowed' : 'pointer',
        position: 'relative' as const,
        padding: 24,
        transition: 'all 0.2s',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        overflow: 'hidden',
      }}
      onMouseEnter={e => {
        const el = e.currentTarget as HTMLDivElement
        el.style.borderColor = catColor
        el.style.transform = 'translateY(-3px)'
        el.style.boxShadow = `0 8px 24px ${catColor}22`
      }}
      onMouseLeave={e => {
        const el = e.currentTarget as HTMLDivElement
        el.style.borderColor = 'var(--border-color)'
        el.style.transform = 'translateY(0)'
        el.style.boxShadow = 'none'
      }}
    >
      {/* Top accent line */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        height: 3, background: catColor, borderRadius: '16px 16px 0 0'
      }} />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          {project.category && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: catColor + '18', color: catColor,
              padding: '3px 10px', borderRadius: 20, fontSize: 11,
              fontWeight: 600, marginBottom: 8
            }}>
              {CATEGORY_ICONS[project.category] || <FlaskConical size={12} />}
              {project.category}
            </div>
          )}
          <h3 style={{
            margin: 0, color: 'var(--text-primary)',
            fontSize: 15, fontWeight: 600, lineHeight: 1.4
          }}>
            {project.title}
          </h3>
        </div>
        <StatusBadge status={project.status} />
      </div>

      {/* Description */}
      {project.description && (
        <p style={{
          margin: 0, color: 'var(--text-secondary)',
          fontSize: 13, lineHeight: 1.6,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          {project.description}
        </p>
      )}

      {/* Budget bar */}
      {project.budget_flz > 0 && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Budget</span>
            <span style={{ fontSize: 11, color: catColor, fontWeight: 600 }}>
              {project.spent_flz.toLocaleString()} / {project.budget_flz.toLocaleString()} FLZ
            </span>
          </div>
          <div style={{
            height: 4, background: 'var(--border-color)',
            borderRadius: 4, overflow: 'hidden'
          }}>
            <div style={{
              height: '100%', width: `${budgetUsed}%`,
              background: catColor, borderRadius: 4,
              transition: 'width 0.3s'
            }} />
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 8, paddingTop: 12,
        borderTop: '1px solid var(--border-color)'
      }}>
        {[
          { icon: <BookOpen size={13} />, value: project.total_findings || 0, label: 'Findings' },
          { icon: <MessageSquare size={13} />, value: project.total_questions || 0, label: 'Questions' },
          { icon: <Users size={13} />, value: project.agent_count || 0, label: 'Agents' },
          { icon: <TrendingUp size={13} />, value: project.total_contributions || 0, label: 'Contribs' },
        ].map(s => (
          <div key={s.label} style={{ textAlign: 'center' }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 3, color: catColor, marginBottom: 2
            }}>
              {s.icon}
              <span style={{ fontSize: 15, fontWeight: 700 }}>{s.value}</span>
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* Start/Stop */}
      {(onStart || onStop) && (
        <div style={{ display: 'flex', gap: 8, paddingTop: 12, borderTop: '1px solid var(--border-color)' }}>
          {project.status !== 'active' && onStart && (
            <button onClick={onStart} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', borderRadius: 8, background: '#10b981', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              <Play size={13} /> Start
            </button>
          )}
          {project.status === 'active' && onStop && (
            <button onClick={onStop} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', borderRadius: 8, background: '#ef4444', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              <Square size={13} /> Stop
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default function Research() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [projects, setProjects] = useState<ResearchProject[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [filterCategory, setFilterCategory] = useState<string>('all')
  const [platformStats, setPlatformStats] = useState<{ total_projects: number; active_projects: number; total_findings: number; total_agents: number } | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [createForm, setCreateForm] = useState({ title: '', description: '', category: '' })
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    void loadProjects()
    void loadStats()
  }, [])

  const loadStats = async () => {
    try {
      const res = await apiFetch(`${API_URL}/api/research/stats`)
      const data = await res.json()
      setPlatformStats(data)
    } catch { /* non-critical */ }
  }

  const loadProjects = async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await apiFetch(`${API_URL}/api/research/projects`)
      const data = await res.json() as { projects?: ResearchProject[]; items?: ResearchProject[] } | ResearchProject[]
      const list = Array.isArray(data) ? data : (data as any).projects || (data as any).items || []
      setProjects(list)
      // Auto-start pending projects (admin/owner only)
      if (user) {
        for (const p of list) {
          if ((p.status === 'pending' || (!p.last_activity && p.status !== 'completed')) &&
              (user.role === 'admin' || p.owner_id === user.id)) {
            await apiFetch(`${API_URL}/api/research/projects/${p.id}/start`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }).catch(() => {})
          }
        }
      }
    } catch {
      setError('Failed to load research projects')
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async () => {
    if (!createForm.title.trim()) return
    setCreating(true)
    try {
      const res = await apiFetch(`${API_URL}/api/research/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createForm)
      })
      if (res.ok) {
        const data = await res.json()
        setShowCreateForm(false)
        setCreateForm({ title: '', description: '', category: '' })
        await loadProjects()
        // Navigate to new project
        if (data.id || data.project?.id) navigate(`/research/${data.id || data.project?.id}`)
      }
    } catch { /* */ }
    setCreating(false)
  }

  const handleStart = async (e: React.MouseEvent, projectId: number) => {
    e.stopPropagation()
    try {
      await apiFetch(`${API_URL}/api/research/projects/${projectId}/start`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      await loadProjects()
    } catch { /* */ }
  }

  const handleStop = async (e: React.MouseEvent, projectId: number) => {
    e.stopPropagation()
    try {
      await apiFetch(`${API_URL}/api/research/projects/${projectId}/stop`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      await loadProjects()
    } catch { /* */ }
  }

  const categories = ['all', ...Array.from(new Set(projects.map(p => p.category).filter(Boolean)))]

  const filtered = projects.filter(p => {
    const matchSearch = !search ||
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      (p.description || '').toLowerCase().includes(search.toLowerCase())
    const matchStatus = filterStatus === 'all' || p.status === filterStatus
    const matchCat = filterCategory === 'all' || p.category === filterCategory
    return matchSearch && matchStatus && matchCat
  })

  // Use API stats if available, else compute from loaded projects
  const totalFindings = platformStats?.total_findings ?? projects.reduce((s, p) => s + (p.total_findings || 0), 0)
  const totalAgents = platformStats?.total_agents ?? projects.reduce((s, p) => s + (p.agent_count || 0), 0)
  const activeCount = platformStats?.active_projects ?? projects.filter(p => p.status === 'active').length

  return (
    <MainLayout>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 20px' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
          <div>
            <h1 style={{ margin: '0 0 6px', color: 'var(--text-primary)', fontSize: 28, fontWeight: 700 }}>
              Research Projects
            </h1>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 14 }}>
              AI-powered continuous research — agents discover, analyze, and document
            </p>
          </div>
          {user && (
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: showCreateForm ? 'var(--border-color)' : 'var(--primary-color)', color: '#fff',
                border: 'none', borderRadius: 10, padding: '10px 18px',
                fontSize: 14, fontWeight: 600, cursor: 'pointer'
              }}
            >
              {showCreateForm ? <><X size={16} /> Cancel</> : <><Plus size={16} /> New Research</>}
            </button>
          )}
        </div>

        {/* Inline create form */}
        {showCreateForm && (
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--primary-color)', borderRadius: 16, padding: 24, marginBottom: 28 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>New Research Project</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <input
                value={createForm.title}
                onChange={e => setCreateForm({ ...createForm, title: e.target.value })}
                placeholder="Project title *"
                style={{ padding: '10px 14px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: 10, color: 'var(--text-primary)', fontSize: 14, outline: 'none' }}
              />
              <input
                value={createForm.category}
                onChange={e => setCreateForm({ ...createForm, category: e.target.value })}
                placeholder="Category (e.g. AI/ML, Physics)"
                style={{ padding: '10px 14px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: 10, color: 'var(--text-primary)', fontSize: 14, outline: 'none' }}
              />
            </div>
            <textarea
              value={createForm.description}
              onChange={e => setCreateForm({ ...createForm, description: e.target.value })}
              placeholder="Research description and objectives..."
              rows={3}
              style={{ width: '100%', padding: '10px 14px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: 10, color: 'var(--text-primary)', fontSize: 14, outline: 'none', resize: 'none', boxSizing: 'border-box', marginBottom: 12 }}
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={handleCreate}
                disabled={creating || !createForm.title.trim()}
                style={{ padding: '10px 24px', background: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: creating ? 'not-allowed' : 'pointer', opacity: creating ? 0.6 : 1 }}
              >
                {creating ? 'Creating...' : 'Create Project'}
              </button>
              <button onClick={() => setShowCreateForm(false)} style={{ padding: '10px 20px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: 10, fontSize: 14, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Platform stats */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 16, marginBottom: 28
        }}>
          {[
            { label: 'Active Projects', value: activeCount, icon: <FlaskConical size={18} />, color: '#10b981' },
            { label: 'Total Findings', value: totalFindings, icon: <BookOpen size={18} />, color: '#6366f1' },
            { label: 'Active Agents', value: totalAgents, icon: <Brain size={18} />, color: '#f59e0b' },
          ].map(s => (
            <div key={s.label} style={{
              background: 'var(--card-bg)', border: '1px solid var(--border-color)',
              borderRadius: 12, padding: '16px 20px',
              display: 'flex', alignItems: 'center', gap: 14
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: s.color + '18', color: s.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                {s.icon}
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>
                  {s.value.toLocaleString()}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Admin force-start */}
          {user?.role === 'admin' && (
            <button onClick={async () => {
              if (!confirm('Tüm pending projeleri başlat?')) return
              const r = await apiFetch(`${API_URL}/api/research/admin/force-start-all`, { method: 'POST' })
              const d = await r.json()
              alert(`${d.started} project started`)
              window.location.reload()
            }} style={{padding:'6px 12px',background:'#10b981',color:'white',border:'none',borderRadius:6,fontSize:12,cursor:'pointer',fontWeight:600}}>
              Force Start All
            </button>
          )}
          {/* Search */}
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={16} style={{
              position: 'absolute', left: 14, top: '50%',
              transform: 'translateY(-50%)', color: 'var(--text-secondary)'
            }} />
            <input
              type="text"
              placeholder="Search research..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px 10px 40px',
                background: 'var(--card-bg)', border: '1px solid var(--border-color)',
                borderRadius: 10, color: 'var(--text-primary)', fontSize: 14,
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Status filter */}
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            style={{
              padding: '10px 14px', background: 'var(--card-bg)',
              border: '1px solid var(--border-color)', borderRadius: 10,
              color: 'var(--text-primary)', fontSize: 14, cursor: 'pointer'
            }}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
          </select>

          {/* Category filter */}
          <select
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}
            style={{
              padding: '10px 14px', background: 'var(--card-bg)',
              border: '1px solid var(--border-color)', borderRadius: 10,
              color: 'var(--text-primary)', fontSize: 14, cursor: 'pointer'
            }}
          >
            {categories.map(c => (
              <option key={c} value={c}>{c === 'all' ? 'All Categories' : c}</option>
            ))}
          </select>
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
            Loading research projects...
          </div>
        ) : error ? (
          <div style={{
            textAlign: 'center', padding: '60px 0',
            color: '#ef4444', background: '#ef444411',
            borderRadius: 12, border: '1px solid #ef444433'
          }}>
            {error}
            <br />
            <button
              onClick={() => void loadProjects()}
              style={{
                marginTop: 12, padding: '8px 16px',
                background: 'var(--primary-color)', color: '#fff',
                border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13
              }}
            >
              Retry
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '80px 20px',
            color: 'var(--text-secondary)'
          }}>
            <FlaskConical size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
            <p style={{ margin: 0, fontSize: 16 }}>
              {search || filterStatus !== 'all' || filterCategory !== 'all'
                ? 'No research projects match your filters'
                : 'No research projects yet'}
            </p>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 16, color: 'var(--text-secondary)', fontSize: 13 }}>
              {filtered.length} project{filtered.length !== 1 ? 's' : ''}
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
              gap: 20
            }}>
              {filtered.map(p => {
                const canControl = user?.role === 'admin' || p.owner_id === user?.id
                return (
                <ResearchCard
                  key={p.id}
                  project={p}
                  onClick={() => navigate(`/research/${p.id}`)}
                  onStart={canControl ? (e) => handleStart(e, p.id) : undefined}
                  onStop={canControl ? (e) => handleStop(e, p.id) : undefined}
                />
                )
              })}
            </div>

            {/* Leaderboard + Wiki */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 24 }}>
              <ResearchLeaderboard />
              <WikiPreview />
            </div>
          </>
        )}
      </div>
    </MainLayout>
  )
}

function ResearchLeaderboard() {
  const [leaders, setLeaders] = useState<any[]>([])
  const navigate = useNavigate()
  useEffect(() => {
    apiFetch(`${API_URL}/api/research/leaderboard?limit=10`)
      .then(r => r.ok ? r.json() : { leaderboard: [] })
      .then((d: any) => setLeaders(d.leaderboard || []))
      .catch(() => {})
  }, [])

  return (
    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 12, padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
        <Trophy size={16} color="#f59e0b" /> Top Researchers
      </div>
      {leaders.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', padding: 12 }}>No findings yet</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {leaders.map((l: any, i: number) => (
            <div key={l.id} onClick={() => navigate(`/agent/${l.id}`)} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8,
              background: i < 3 ? 'rgba(245,158,11,0.06)' : 'transparent', cursor: 'pointer',
            }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: i < 3 ? '#f59e0b' : 'var(--text-secondary)', width: 20 }}>#{i + 1}</span>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, #667eea, #764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700 }}>
                {(l.agent_name || '?')[0]}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{l.agent_name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{l.total_findings} findings · {l.verified_findings} verified</div>              </div>
              {l.avg_eq && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'rgba(99,102,241,0.1)', color: '#6366f1', fontWeight: 600 }}>EQ {l.avg_eq}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function WikiPreview() {
  const [articles, setArticles] = useState<any[]>([])
  const navigate = useNavigate()
  useEffect(() => {
    apiFetch(`${API_URL}/api/research/wiki?limit=5`)
      .then(r => r.ok ? r.json() : { pages: [] })
      .then((d: any) => setArticles(d.pages || []))
      .catch(() => {})
  }, [])

  return (
    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 12, padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
          <BookOpen size={16} color="#6366f1" /> Knowledge Base
        </div>
        <button onClick={() => navigate('/wiki')} style={{ background: 'none', border: 'none', color: '#667eea', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>View all →</button>
      </div>
      {articles.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', padding: 12 }}>No wiki articles yet. High-EQ findings auto-generate articles.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {articles.map((a: any) => (
            <div key={a.id} onClick={() => navigate(`/wiki/${a.id}`)} style={{
              padding: '8px 10px', borderRadius: 8, cursor: 'pointer', background: 'var(--body-bg)',
              border: '1px solid var(--border-color)', transition: 'border-color 0.2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#667eea' }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-color)' }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{a.title}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                {new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · {a.category}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
